// Root application: auth gate -> live session (signaling + WebRTC) -> explorer.
import { useCallback, useEffect, useRef, useState } from 'react';
import { DRIVES_LIST_ERROR, SERVER_URL, WS_URL, listPCs, type AuthResponse, type PCInfo } from './api';
import { AuthGate } from './components/AuthGate';
import { EncVaultModal } from './components/EncVaultModal';
import { DrivePicker } from './components/DrivePicker';
import { FolderPicker } from './components/FolderPicker';
import { FileExplorer } from './components/FileExplorer';
import { SyncSetupModal } from './components/SyncSetupModal';
import { SyncJobsPanel } from './components/SyncJobsPanel';
import { PreviewModal, saveBlob, type PreviewState } from './components/PreviewModal';
import { canNativeShareFile, shareFileNative } from './nativeShare';
import { StatusPills } from './components/StatusPills';
import { ConnectivityLogPanel } from './components/ConnectivityLogPanel';
import { logIceFailureDiag } from './iceFailureDiag';
import {
  formatGatheringSummary,
  sanitizeUserLog,
  userDialPhaseLabel,
} from './userLogSanitize';
import { DATA_CHANNEL_LABEL, PeerSession, validCandidateInit } from './peer';
import { dialIceSteps, fetchIceServers } from './iceConfig';
import { encryptFile, decryptFile, isHcloud } from './crypto';
import { canStream, createStreamSink, type StreamSink } from './stream';
import {
  ICE_SERVERS,
  listFilesRequest,
  getThumbRequest,
  XFER_CHUNK,
  XFER_LABEL,
  type FileNode,
  type HostInfoMessage,
  type PlanTier,
  type SignalingMessage,
  type ThumbMessage,
  type XferFrame,
  type XferProgress,
} from './protocol';
import { SignalingClient, type SignalingStatus } from './signaling';
import { APP_NAME } from './branding';
import { mergeListResponse } from './fileTreeNav';
import {
  EXPLORER_CONNECT_TIMEOUT_MS,
  LIST_FILES_TIMEOUT_MS,
  P2P_IDLE_MS,
  P2P_OFFER_QUIET_MS,
  SESSION_IDLE_MS,
  DIAL_ATTEMPTS_PER_PHASE,
  DIAL_PHASE_GAP_MS,
} from './p2pConfig';
import { isPaidTier, PAID_FEATURE_HINT } from './planTier';
import { runProfileDownload, runProfileJob, type SyncMode } from './sync/syncEngine';
import { loadDirHandle } from './sync/localDirStore';
import type { SyncProfile } from './sync/syncSettings';
import './styles.css';

type Phase = 'auth' | 'picking' | 'picking-folder' | 'connecting' | 'online' | 'linked' | 'error';

interface SessionState {
  phase: Phase;
  status: SignalingStatus;
  hostOnline: boolean;
  error: string | null;
  /** Host declined our offer — another session or upload is using the PC. */
  hostBusy: boolean;
}

export default function App(): JSX.Element {
  const [auth, setAuth] = useState<AuthResponse | null>(null);
  const [session, setSession] = useState<SessionState>({
    phase: 'auth',
    status: 'idle',
    hostOnline: false,
    error: null,
    hostBusy: false,
  });
  const hostBusyRetryRef = useRef<number | null>(null);
  const [tree, setTree] = useState<FileNode | null>(null);
  const [busy, setBusy] = useState(false);
  // Transport that actually carried the P2P link: null until connected.
  const [icePath, setIcePath] = useState<'lan' | 'host' | 'stun' | 'turn' | 'unknown' | null>(null);
  const [iceIpFamily, setIceIpFamily] = useState<'ipv6' | 'ipv4' | '?' | null>(null);
  const [iceDialPhase, setIceDialPhase] = useState<'stun' | 'turn' | null>(null);
  const [p2pDetail, setP2pDetail] = useState<string | null>(null);
  const dialAttemptRef = useRef(false);
  // Phase-2 file transfer: live progress + full-file preview modal.
  const [transfer, setTransfer] = useState<XferProgress | null>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  // Item 6: the "My PCs" picker - PCs the caller owns or has a folder shared with.
  const [pcs, setPcs] = useState<PCInfo[]>([]);
  const [selectedPC, setSelectedPC] = useState<PCInfo | null>(null);
  const [activeShare, setActiveShare] = useState<{ path: string; name: string } | null>(null);
  // Item 2: opt-in .hcloud encryption on upload (default OFF).
  const [encryptUploads, setEncryptUploads] = useState(false);
  // Item 2: prompt shown when Encrypt is switched on but this device has no
  // unlocked encryption password yet ('init' = none exists, 'unlock' = verify).
  const [encPrompt, setEncPrompt] = useState<'init' | 'unlock' | null>(null);
  const [connLogLines, setConnLogLines] = useState<string[]>([]);
  const [hostPlanTier, setHostPlanTier] = useState<PlanTier | null>(null);
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  const pendingHostListRef = useRef<((node: FileNode) => void) | null>(null);
  const syncBlobWaitRef = useRef<{
    resolve: (b: Blob) => void;
    reject: (e: Error) => void;
  } | null>(null);
  const syncTransferActiveRef = useRef(false);
  const [connLogExpanded, setConnLogExpanded] = useState(false);
  const [syncSetupOpen, setSyncSetupOpen] = useState(false);
  const [syncJobsTick, setSyncJobsTick] = useState(0);

  const appendConnLog = useCallback((line: string) => {
    const ts = new Date().toLocaleTimeString();
    setConnLogLines((prev) => [...prev.slice(-150), `[${ts}] ${sanitizeUserLog(line)}`]);
  }, []);
  const appendConnLogRef = useRef(appendConnLog);
  appendConnLogRef.current = appendConnLog;

  const signalingRef = useRef<SignalingClient | null>(null);
  const peerRef = useRef<PeerSession | null>(null);
  const pendingCandidates = useRef<RTCIceCandidateInit[]>([]);
  // Item 6: the userId we dial — the selected PC's owner (may differ from ours).
  const targetRef = useRef<string>('');
  /** Registered PC id — routes signaling to one desktop when the owner has several. */
  const pcIdRef = useRef<string>('');
  /** Host folder path key for the share being browsed (when the PC has one or more roots). */
  const selectedSharePathRef = useRef<string>('');
  const selectedPCRef = useRef<PCInfo | null>(null);
  selectedPCRef.current = selectedPC;
  // Item 2: set when the in-flight download is a .hcloud container to decrypt.
  const decryptPending = useRef(false);
  const answerApplied = useRef(false);
  /** Prevents two parallel startPeer() calls (duplicate offers confuse the host). */
  const peerStartingRef = useRef(false);
  /** Bumped on teardown / navigation so in-flight dial loops exit. */
  const dialGenerationRef = useRef(0);
  /** After p2p-release, WebView2 on Windows needs a beat before the next offer. */
  const lastP2pReleaseAtRef = useRef(0);
  /** Dial generation captured when the current offer was sent — stale answers are ignored. */
  const offerDialGenRef = useRef(0);
  const iceRef = useRef<RTCIceServer[]>(ICE_SERVERS);
  const authRef = useRef<AuthResponse | null>(null);
  authRef.current = auth;
  const signalingOpenRef = useRef(false);
  /** Last user/P2P activity — used for optional idle teardown of WebRTC. */
  const p2pActivityRef = useRef(Date.now());
  /** Last UI/session activity — full logout when exceeded (closes signaling WS). */
  const sessionActivityRef = useRef(Date.now());
  /** Suppress "P2P link lost" UI when we closed the peer on purpose (idle timeout). */
  const silentPeerCloseRef = useRef(false);
  /** Set true to stop the in-flight upload loop and notify the host. */
  const uploadCancelRef = useRef(false);
  const phaseRef = useRef<Phase>('auth');
  phaseRef.current = session.phase;
  const ensurePeerRef = useRef<(() => Promise<PeerSession | null>) | null>(null);
  /** Browser back stack: 0 = My Drives, 1 = Shared folders, 2 = file explorer. */
  const navHistoryLevelRef = useRef<0 | 1 | 2>(0);
  /** How many pushState entries we added this session (rewound on login/logout). */
  const historyPushCountRef = useRef(0);
  /** Ignore popstate while collapsing the stack on login/logout. */
  const navHistoryResetRef = useRef(false);

  const applyHistoryLevel = useCallback((level: 0 | 1 | 2, mode: 'push' | 'replace') => {
    const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const state = { hcNav: level };
    if (mode === 'replace') {
      window.history.replaceState(state, '', url);
    } else {
      window.history.pushState(state, '', url);
      historyPushCountRef.current += 1;
    }
    navHistoryLevelRef.current = level;
  }, []);

  /** Collapse in-app history pushes and replace the current entry (login/logout). */
  const resetBrowserNavStack = useCallback((state: { hcNav?: 0 | 1 | 2; hcAuth?: true }) => {
    const url = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const rewind = historyPushCountRef.current;
    historyPushCountRef.current = 0;
    navHistoryLevelRef.current = typeof state.hcNav === 'number' ? state.hcNav : 0;
    navHistoryResetRef.current = true;
    if (rewind > 0) {
      window.history.go(-rewind);
    }
    window.setTimeout(() => {
      window.history.replaceState(state, '', url);
      navHistoryResetRef.current = false;
    }, rewind > 0 ? 50 : 0);
  }, []);

  const touchSessionActivity = useCallback(() => {
    sessionActivityRef.current = Date.now();
  }, []);

  const touchP2pActivity = useCallback(() => {
    p2pActivityRef.current = Date.now();
  }, []);

  const clearHostBusyRetry = useCallback(() => {
    if (hostBusyRetryRef.current !== null) {
      window.clearTimeout(hostBusyRetryRef.current);
      hostBusyRetryRef.current = null;
    }
  }, []);

  const scheduleHostBusyRetry = useCallback(() => {
    clearHostBusyRetry();
    hostBusyRetryRef.current = window.setTimeout(() => {
      hostBusyRetryRef.current = null;
      if (!pcIdRef.current || peerRef.current || peerStartingRef.current) return;
      if (
        phaseRef.current === 'picking' ||
        phaseRef.current === 'picking-folder' ||
        phaseRef.current === 'auth'
      ) {
        return;
      }
      const sig = signalingRef.current;
      if (sig && signalingOpenRef.current) void startPeerRef.current?.(sig);
    }, 5000);
  }, [clearHostBusyRetry]);

  const startPeerRef = useRef<(sig: SignalingClient) => Promise<void>>(async () => {});

  /** Explorer folder path for refresh / subfolder listing ("" = share root). */
  const explorerRelPathRef = useRef('');
  const listWaitGenRef = useRef(0);
  const listWaitTimerRef = useRef<number | null>(null);
  const explorerConnectGenRef = useRef(0);

  // --- Phase-2 transfer machinery -------------------------------------------
  /** The "xfer" data channel (binary file frames); null until linked. */
  const xferRef = useRef<RTCDataChannel | null>(null);
  /** Accumulated download chunks; meta set = a download is in flight. */
  const dlChunks = useRef<BlobPart[]>([]);
  const dlMeta = useRef<{ name: string; size: number; mime: string } | null>(null);
  /** 'save' = auto-download when complete; 'open' = show the preview modal. */
  const dlMode = useRef<'save' | 'open' | 'share'>('save');
  /** Active MediaSource sink for a streaming (video/audio) open; null otherwise. */
  const streamSink = useRef<StreamSink | null>(null);
  /** True while a closed stream's leftover frames must be dropped (not buffered). */
  const dlDiscard = useRef(false);
  /** Upload in flight (guard against concurrent transfers). */
  const upActive = useRef(false);
  /** Resolves when the host acks an upload (put-done / xfer-error). */
  const putWait = useRef<(() => void) | null>(null);
  const putOk = useRef(false);

  // Prime ICE from the server early; startPeer() refreshes again before each dial.
  useEffect(() => {
    void fetchIceServers(SERVER_URL).then((servers) => {
      iceRef.current = servers;
    });
  }, []);

  const waitP2pOfferQuiet = useCallback(async (): Promise<void> => {
    const pauseMs = Math.max(0, P2P_OFFER_QUIET_MS - (Date.now() - lastP2pReleaseAtRef.current));
    if (pauseMs > 0) {
      await new Promise((r) => window.setTimeout(r, pauseMs));
    }
  }, []);

  const answerAppliesToCurrentOffer = useCallback((): boolean => {
    return offerDialGenRef.current === dialGenerationRef.current && Boolean(peerRef.current);
  }, []);

  // --- teardown: peer only vs full session (keep signaling on "My PCs") ----
  const teardownPeer = useCallback((opts?: { preserveExplorer?: boolean; silent?: boolean }) => {
    dialGenerationRef.current += 1;
    peerStartingRef.current = false;
    setIceDialPhase(null);
    const sig = signalingRef.current;
    const releaseTarget = targetRef.current;
    const releasePcId = pcIdRef.current;
    const hadPeer = Boolean(peerRef.current);
    if (sig && releaseTarget && releasePcId && signalingOpenRef.current) {
      const sinceRelease = Date.now() - lastP2pReleaseAtRef.current;
      // Always release when closing an active peer; otherwise debounce duplicate releases.
      if (hadPeer || sinceRelease >= 250) {
        sig.send({ type: 'p2p-release', target: releaseTarget, pcId: releasePcId });
        lastP2pReleaseAtRef.current = Date.now();
      }
    }
    if (opts?.silent) silentPeerCloseRef.current = true;
    peerRef.current?.close();
    peerRef.current = null;
    pendingCandidates.current = [];
    answerApplied.current = false;
    xferRef.current = null;
    dlChunks.current = [];
    dlMeta.current = null;
    dlDiscard.current = false;
    if (streamSink.current) {
      streamSink.current.close();
      streamSink.current = null;
    }
    upActive.current = false;
    uploadCancelRef.current = false;
    if (putWait.current) {
      putOk.current = false;
      putWait.current();
      putWait.current = null;
    }
    setTransfer(null);
    if (!opts?.preserveExplorer) {
      setPreview((p) => {
        if (p) URL.revokeObjectURL(p.url);
        return null;
      });
      setTree(null);
    }
    setBusy(false);
    setIcePath(null);
    setIceIpFamily(null);
    setP2pDetail(null);
    if (opts?.silent) {
      setSession((s) => (s.phase === 'linked' ? { ...s, phase: 'online' } : s));
    }
  }, []);

  const clearListWait = useCallback(() => {
    listWaitGenRef.current += 1;
    if (listWaitTimerRef.current !== null) {
      window.clearTimeout(listWaitTimerRef.current);
      listWaitTimerRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearHostBusyRetry();
    clearListWait();
    // p2p-release must go out before the signaling socket is closed (sign-out).
    teardownPeer();
    signalingRef.current?.disconnect();
    signalingRef.current = null;
    signalingOpenRef.current = false;
  }, [teardownPeer, clearHostBusyRetry, clearListWait]);

  useEffect(() => () => teardown(), [teardown]);

  const leaveExplorerDueToFailure = useCallback(
    (message: string) => {
      clearListWait();
      clearHostBusyRetry();
      teardownPeer();
      setTree(null);
      explorerRelPathRef.current = '';
      const pc = selectedPCRef.current;
      const multi = Boolean(pc && pc.folders.length > 1);
      setActiveShare(null);
      selectedSharePathRef.current = '';
      targetRef.current = '';
      pcIdRef.current = '';
      if (!multi) {
        setSelectedPC(null);
      }
      setSession((s) => ({
        ...s,
        phase: multi ? 'picking-folder' : 'picking',
        hostOnline: false,
        hostBusy: false,
        error: message,
      }));
      const creds = authRef.current;
      if (creds) {
        listPCs(creds.token)
          .then(setPcs)
          .catch(() => setSession((s) => ({ ...s, error: DRIVES_LIST_ERROR })));
      }
      applyHistoryLevel(multi ? 1 : 0, 'replace');
    },
    [clearHostBusyRetry, clearListWait, teardownPeer, applyHistoryLevel]
  );

  const bumpListWait = useCallback(() => {
    clearListWait();
    const gen = ++listWaitGenRef.current;
    listWaitTimerRef.current = window.setTimeout(() => {
      if (listWaitGenRef.current !== gen) return;
      if (phaseRef.current !== 'linked') return;
      leaveExplorerDueToFailure(
        'Folder listing timed out. Make sure the desktop host is running, then open the share again.'
      );
    }, LIST_FILES_TIMEOUT_MS);
  }, [clearListWait, leaveExplorerDueToFailure]);

  const applyFilesChannelMessage = useCallback(
    (parsed: { node?: FileNode; error?: string; type?: string; path?: string; data?: string; mime?: string; planTier?: PlanTier; pcId?: string }) => {
      if (parsed.type === 'host-info') {
        const info = parsed as HostInfoMessage;
        if (info.planTier === 'paid' || info.planTier === 'free') {
          setHostPlanTier(info.planTier);
        }
        return;
      }
      if (parsed.type === 'thumb' && parsed.path) {
        const thumb = parsed as ThumbMessage;
        if (thumb.data && !thumb.error) {
          const mime = thumb.mime ?? 'image/jpeg';
          setThumbUrls((prev) => ({
            ...prev,
            [thumb.path]: `data:${mime};base64,${thumb.data}`,
          }));
        }
        return;
      }
      if (parsed.node && pendingHostListRef.current) {
        pendingHostListRef.current(parsed.node);
        pendingHostListRef.current = null;
        return;
      }
      if (parsed.error) {
        if (pendingHostListRef.current) {
          pendingHostListRef.current = null;
        }
        leaveExplorerDueToFailure(parsed.error ?? 'Host rejected the folder listing');
        return;
      }
      if (parsed.node) {
        clearListWait();
        setTree((prev) => mergeListResponse(prev, parsed.node!));
      }
    },
    [clearListWait, leaveExplorerDueToFailure]
  );

  const requestFileList = useCallback(
    (relPath: string = explorerRelPathRef.current) => {
      touchP2pActivity();
      const frame = listFilesRequest(selectedSharePathRef.current || undefined, relPath);
      const sent = peerRef.current?.send(frame);
      if (sent && relPath === '') bumpListWait();
    },
    [touchP2pActivity, bumpListWait]
  );

  useEffect(() => {
    if (!auth || !activeShare) return;
    if (session.phase === 'picking' || session.phase === 'picking-folder' || session.phase === 'auth') {
      return;
    }
    if (session.phase === 'linked' && tree) return;
    const token = ++explorerConnectGenRef.current;
    const t = window.setTimeout(() => {
      if (explorerConnectGenRef.current !== token) return;
      if (phaseRef.current === 'linked') return;
      leaveExplorerDueToFailure(
        'Timed out connecting to your drive. Check that the desktop host is running, then try again.'
      );
    }, EXPLORER_CONNECT_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [auth, activeShare, session.phase, tree, leaveExplorerDueToFailure]);

  // Close WebRTC after idle time (configurable via VITE_P2P_IDLE_MS). Return to
  // drive/folder pickers when the user was browsing a share.
  useEffect(() => {
    if (P2P_IDLE_MS <= 0) return;
    const id = window.setInterval(() => {
      if (!peerRef.current) return;
      if (phaseRef.current !== 'linked') return;
      if (upActive.current || dlMeta.current) return;
      if (Date.now() - p2pActivityRef.current < P2P_IDLE_MS) return;
      if (selectedSharePathRef.current) {
        leaveExplorerDueToFailure(
          'Your drive connection closed after inactivity. Open the folder again when you need your files.'
        );
      } else {
        teardownPeer({ preserveExplorer: true, silent: true });
      }
    }, 2000);
    return () => window.clearInterval(id);
  }, [teardownPeer, leaveExplorerDueToFailure]);

  // --- xfer channel: download/upload state machine --------------------------

  /**
   * All messages arriving on the "xfer" channel.
   * Binary = downloaded chunk (accumulate); text = JSON transfer frame.
   * Declared before startPeer so the channel-open handler can reference it.
   */
  const handleXferFrame = useCallback((ev: MessageEvent) => {
    touchP2pActivity();
    const data = ev.data;
    if (typeof data !== 'string') {
      // One downloaded chunk (frames are ordered, so appending is enough).
      if (!dlMeta.current) return;
      const buf = data as ArrayBuffer;
      // Streaming media: feed the ordered bytes straight into the MediaSource
      // so playback begins before the whole file has arrived.
      if (streamSink.current) {
        streamSink.current.append(buf);
        setTransfer((t) => (t && t.dir === 'down' ? { ...t, done: t.done + buf.byteLength } : t));
        return;
      }
      // The user closed a streaming preview mid-transfer: drop the remaining
      // frames instead of buffering the rest of the file into memory.
      if (dlDiscard.current) {
        setTransfer((t) => (t && t.dir === 'down' ? { ...t, done: t.done + buf.byteLength } : t));
        return;
      }
      dlChunks.current.push(buf);
      setTransfer((t) => (t && t.dir === 'down' ? { ...t, done: t.done + buf.byteLength } : t));
      return;
    }
    let frame: XferFrame;
    try {
      frame = JSON.parse(data) as XferFrame;
    } catch {
      return;
    }
    if ('op' in frame) return; // host never sends request frames
    switch (frame.type) {
      case 'file-meta': {
        dlMeta.current = { name: frame.name, size: frame.size, mime: frame.mime };
        dlChunks.current = [];
        dlDiscard.current = false;
        // Defensive: a previous stream must already be released at file-end,
        // but never let two sinks share one transfer.
        if (streamSink.current) {
          streamSink.current.close();
          streamSink.current = null;
        }
        setTransfer({ dir: 'down', name: frame.name, total: frame.size, done: 0 });
        // Item: stream video/audio instead of download-then-play. Only when the
        // user chose to OPEN (not save) and the file isn't an encrypted
        // container (those must be fully buffered to decrypt). Falls back to
        // the normal buffer path when the browser can't stream this container.
        if (dlMode.current === 'open' && !decryptPending.current && canStream(frame.mime)) {
          const sink = createStreamSink(frame.mime);
          if (sink) {
            streamSink.current = sink;
            const placeholder = new File([], frame.name, { type: sink.mime });
            setPreview((prev) => {
              if (prev) URL.revokeObjectURL(prev.url);
              return { file: placeholder, url: sink.url, mime: sink.mime, streaming: true, totalSize: frame.size };
            });
          }
        }
        break;
      }
      case 'file-end': {
        const meta = dlMeta.current;
        dlMeta.current = null;
        // Streaming media finished arriving — signal EOS and release the sink
        // REF (not the URL: the open preview still plays from it; the preview's
        // own onClose revokes it). Leaving the ref set would poison every
        // later download: its bytes would append to this ended MediaSource
        // instead of dlChunks, so no preview/download could ever complete.
        if (streamSink.current) {
          streamSink.current.end();
          streamSink.current = null;
          dlDiscard.current = false;
          setTransfer(null);
          break;
        }
        setTransfer(null);
        if (!meta || dlDiscard.current || dlChunks.current.length === 0) {
          dlChunks.current = [];
          dlDiscard.current = false;
          break;
        }
        let mime = meta.mime || 'application/octet-stream';
        if (mime === 'application/octet-stream' && meta.name.toLowerCase().endsWith('.pdf')) {
          mime = 'application/pdf';
        }
        const file = new File(dlChunks.current, meta.name, { type: mime });
        dlChunks.current = [];
        if (syncBlobWaitRef.current) {
          const wait = syncBlobWaitRef.current;
          syncBlobWaitRef.current = null;
          syncTransferActiveRef.current = false;
          setTransfer(null);
          wait.resolve(file);
          break;
        }
        void (async () => {
          let out = file;
          if (decryptPending.current) {
            decryptPending.current = false;
            const encPassword = window.sessionStorage.getItem('hc_enc_password') ?? '';
            try {
              out = await decryptFile(file, encPassword);
            } catch (e) {
              setSession((s) => ({ ...s, error: `Decrypt failed: ${String(e)}` }));
              return;
            }
          }
          if (dlMode.current === 'share') {
            try {
              if (canNativeShareFile(out)) {
                await shareFileNative(out);
              } else {
                setSession((s) => ({
                  ...s,
                  error: 'Share is not available for this file on this device (try Download)',
                }));
              }
            } catch (e) {
              setSession((s) => ({ ...s, error: `Share failed: ${String(e)}` }));
            }
          } else if (dlMode.current === 'open') {
            const url = URL.createObjectURL(out);
            setPreview((prev) => {
              if (prev) URL.revokeObjectURL(prev.url);
              return { file: out, url, mime: out.type };
            });
          } else {
            saveBlob(out.name, out);
          }
        })();
        break;
      }
      case 'put-done': {
        setTransfer(null);
        // The PC now holds a new file — re-fetch the tree so it appears.
        requestFileList(explorerRelPathRef.current);
        const resolve = putWait.current;
        putWait.current = null;
        putOk.current = true;
        resolve?.();
        break;
      }
      case 'xfer-error': {
        if (syncBlobWaitRef.current) {
          const wait = syncBlobWaitRef.current;
          syncBlobWaitRef.current = null;
          syncTransferActiveRef.current = false;
          setTransfer(null);
          wait.reject(new Error(frame.error ?? 'transfer failed'));
          break;
        }
        dlMeta.current = null;
        dlChunks.current = [];
        dlDiscard.current = false;
        if (streamSink.current) {
          streamSink.current.close();
          streamSink.current = null;
          setPreview((prev) => {
            if (prev && prev.streaming) {
              URL.revokeObjectURL(prev.url);
              return null;
            }
            return prev;
          });
        }
        setTransfer(null);
        setSession((s) => ({ ...s, error: `Transfer failed: ${frame.error}` }));
        const resolve = putWait.current;
        putWait.current = null;
        putOk.current = false;
        resolve?.();
        break;
      }
      default:
        break;
    }
  }, [touchP2pActivity]);

  const cancelTransfer = useCallback(() => {
    touchP2pActivity();
    if (upActive.current) {
      uploadCancelRef.current = true;
      peerRef.current?.sendXfer(JSON.stringify({ op: 'put-cancel' }));
      if (putWait.current) {
        putOk.current = false;
        putWait.current();
        putWait.current = null;
      }
      return;
    }
    if (dlMeta.current) {
      dlDiscard.current = true;
      dlMeta.current = null;
      dlChunks.current = [];
      setTransfer(null);
      if (streamSink.current) {
        streamSink.current.close();
        streamSink.current = null;
      }
    }
  }, [touchP2pActivity]);

  /** Starts a download: ask the host for one shared-folder path. */
  const requestFile = useCallback(
    (node: FileNode, mode: 'save' | 'open' | 'share') => {
      void (async () => {
      const peer = await (ensurePeerRef.current?.() ?? Promise.resolve(null));
      // Feedback instead of a silent no-op: the big tile button is not
      // disabled during a transfer, so a click here used to do NOTHING
      // with no indication why.
      if (!peer) {
        setSession((s) => ({ ...s, error: 'Transfer channel is not ready yet' }));
        return;
      }
      if (dlMeta.current || upActive.current) {
        setSession((s) => ({ ...s, error: 'Another transfer is in progress — wait for it to finish' }));
        return;
      }
// .hcloud files require unlock: if we're opening a NEW node that doesn't
      // already have a user-set password, prompt immediately before transfer.
      if (
        (mode === 'open' || mode === 'share') &&
        node.name &&
        node.name.endsWith('.hcloud')
      ) {
        const encPassword = window.sessionStorage.getItem('hc_enc_password');
        if (!encPassword) {
          setSession((s) => ({ ...s, error: 'Unlock required' }));
          return;
        }
      }
      dlMode.current = mode;
      decryptPending.current = isHcloud(node.name);
      dlMeta.current = { name: node.name, size: node.size ?? 0, mime: '' };
      setTransfer({ dir: 'down', name: node.name, total: node.size ?? 0, done: 0 });
      if (!peer.sendXfer(JSON.stringify({ op: 'get', path: node.path }))) {
        dlMeta.current = null;
        setTransfer(null);
        setSession((s) => ({ ...s, error: 'Transfer channel not open yet' }));
      }
      })();
    },
    []
  );

  const openFile = useCallback((node: FileNode) => requestFile(node, 'open'), [requestFile]);
  const downloadFile = useCallback((node: FileNode) => requestFile(node, 'save'), [requestFile]);
  const shareFile = useCallback((node: FileNode) => requestFile(node, 'share'), [requestFile]);

  /** Creates a folder under the shared root (relative path from PWA). */
  const moveEntry = useCallback((fromPath: string, toDirPath: string) => {
    void (ensurePeerRef.current?.() ?? Promise.resolve(null)).then((peer) => {
    if (!peer) {
      setSession((s) => ({ ...s, error: 'Not connected — try Refresh' }));
      return;
    }
    const base = fromPath.split('/').pop();
    if (!peer || !base) return;
    const to = toDirPath ? `${toDirPath}/${base}` : base;
    if (to === fromPath) return;
    if (toDirPath === fromPath || toDirPath.startsWith(`${fromPath}/`)) {
      setSession((s) => ({ ...s, error: 'Cannot move a folder into itself' }));
      return;
    }
    if (!peer.send(JSON.stringify({ cmd: 'MOVE', from: fromPath, to }))) {
      setSession((s) => ({ ...s, error: 'Control channel not open — try Refresh' }));
    }
    });
  }, []);

  const deleteEntry = useCallback((relPath: string, kind: 'dir' | 'file') => {
    if (!relPath) return;
    const msg =
      kind === 'dir'
        ? 'Delete this folder and everything inside it? You can recover it later only from the Trash bin on the host.'
        : 'Delete this file? You can recover it later only from the Trash bin on the host.';
    if (!window.confirm(msg)) return;
    void (ensurePeerRef.current?.() ?? Promise.resolve(null)).then((peer) => {
      if (!peer) {
        setSession((s) => ({ ...s, error: 'Not connected — try Refresh' }));
        return;
      }
      if (!peer.send(JSON.stringify({ cmd: 'DELETE', path: relPath }))) {
        setSession((s) => ({ ...s, error: 'Control channel not open — try Refresh' }));
      }
    });
  }, []);

  const createFolder = useCallback((parentPath: string, name: string) => {
    const trimmed = name.trim().replace(/[/\\]/g, '');
    if (!trimmed) return;
    const rel = parentPath ? `${parentPath}/${trimmed}` : trimmed;
    void (ensurePeerRef.current?.() ?? Promise.resolve(null)).then((peer) => {
      if (!peer) {
        setSession((s) => ({ ...s, error: 'Not connected — try Refresh' }));
        return;
      }
      if (!peer.send(JSON.stringify({ cmd: 'MKDIR', path: rel }))) {
        setSession((s) => ({ ...s, error: 'Control channel not open — try Refresh' }));
      }
    });
  }, []);

  /** Uploads picked phone files into the given folder (relative to share root). */
  const uploadFiles = useCallback(async (
    files: File[],
    destDir: string,
    opts?: { absoluteRel?: string }
  ) => {
    touchP2pActivity();
    if (files.length === 0) {
      setSession((s) => ({ ...s, error: 'No files selected' }));
      return;
    }
    const folderUpload = files.some((f) => {
      const wr = (f as File & { webkitRelativePath?: string }).webkitRelativePath;
      return Boolean(wr && wr.includes('/'));
    });
    if (folderUpload && !isPaidTier(hostPlanTier)) {
      setSession((s) => ({ ...s, error: PAID_FEATURE_HINT }));
      return;
    }
    if (dlMeta.current) {
      setSession((s) => ({ ...s, error: 'Finish or cancel the download before uploading' }));
      return;
    }
    if (upActive.current || syncTransferActiveRef.current) {
      setSession((s) => ({ ...s, error: 'An upload is already in progress' }));
      return;
    }
    const peer = await (ensurePeerRef.current?.() ?? Promise.resolve(null));
    if (!peer) {
      setSession((s) => ({ ...s, error: 'Not connected — try again in a moment' }));
      return;
    }
    for (let i = 0; i < 80; i++) {
      if (peer.transferReady) break;
      await new Promise((r) => setTimeout(r, 50));
    }
    if (!peer.transferReady) {
      setSession((s) => ({
        ...s,
        error: 'Transfer channel not ready — tap Refresh or wait a moment',
      }));
      return;
    }
    upActive.current = true;
    uploadCancelRef.current = false;
    try {
      for (const file of files) {
        if (uploadCancelRef.current) break;
        // Item 2: opt-in .hcloud encryption (default off).
        let outFile = file;
        if (encryptUploads) {
          const encPassword = window.sessionStorage.getItem('hc_enc_password') ?? '';
          if (!encPassword) throw new Error('encryption enabled but no encryption password set');
          outFile = await encryptFile(file, encPassword);
        }
        setTransfer({ dir: 'up', name: outFile.name, total: outFile.size, done: 0 });
        // Register the ack waiter BEFORE announcing, so a fast host cannot
        // send put-done before we are listening for it.
        const acked = new Promise<void>((resolve) => {
          putWait.current = resolve;
        });
        putOk.current = false;
        const wr = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
        let uploadName = outFile.name;
        if (wr && wr.includes('/')) {
          uploadName = wr.replace(/\\/g, '/');
        }
        const dir = destDir.trim().replace(/\\/g, '/');
        const putFrame: { op: 'put-begin'; name: string; size: number; dir?: string } = {
          op: 'put-begin',
          name: uploadName,
          size: outFile.size,
        };
        if (opts?.absoluteRel) {
          putFrame.name = opts.absoluteRel.replace(/\\/g, '/');
        } else if (uploadName.includes('/')) {
          const full = dir ? `${dir}/${uploadName}` : uploadName;
          putFrame.name = full;
        } else if (dir) {
          putFrame.dir = dir;
        }
        const p = peerRef.current;
        if (!p) throw new Error('not connected');
        // put-begin on the control channel: some stacks deliver JSON reliably only on "files".
        const beginJson = JSON.stringify(putFrame);
        if (!p.send(beginJson)) {
          if (!p.sendXfer(beginJson)) {
            throw new Error('control and transfer channels not open');
          }
        }
        let offset = 0;
        while (offset < outFile.size) {
          if (uploadCancelRef.current) {
            peerRef.current?.sendXfer(JSON.stringify({ op: 'put-cancel' }));
            throw new Error('cancelled');
          }
          touchP2pActivity();
          const chunk = outFile.slice(offset, offset + XFER_CHUNK);
          const buf = await chunk.arrayBuffer();
          // Backpressure: don't outrun the SCTP send buffer.
          while ((peerRef.current?.xferBuffered ?? 0) > 8 * 1024 * 1024) {
            await new Promise((r) => setTimeout(r, 80));
          }
          const live = peerRef.current;
          if (!live?.sendXfer(buf)) throw new Error('transfer channel closed mid-upload');
          offset += buf.byteLength;
          const at = offset;
          setTransfer((t) => (t && t.dir === 'up' ? { ...t, done: at } : t));
        }
        if (uploadCancelRef.current) {
          peerRef.current?.sendXfer(JSON.stringify({ op: 'put-cancel' }));
          throw new Error('cancelled');
        }
        // Watchdog: if the host never acks (dead channel without a close
        // event), don't leave upActive/transfer stuck forever — that grays
        // out every "Open" button and silently swallows later clicks.
        const ackTimeout = new Promise<'timeout'>((r) =>
          setTimeout(() => r('timeout'), 60_000)
        );
        const winner = await Promise.race([
          acked.then(() => 'ack' as const),
          ackTimeout,
        ]);
        if (winner === 'timeout') {
          throw new Error('host did not confirm the upload (timed out)');
        }
        if (!putOk.current) throw new Error('host rejected the upload');
      }
    } catch (e) {
      const msg = String(e);
      if (msg.includes('cancelled')) {
        setSession((s) => ({ ...s, error: null }));
      } else {
        setSession((s) => ({ ...s, error: `Upload failed: ${msg}` }));
      }
    } finally {
      upActive.current = false;
      uploadCancelRef.current = false;
      putWait.current = null;
      setTransfer(null);
      touchP2pActivity();
      requestFileList(explorerRelPathRef.current);
    }
    // NOTE: `encryptUploads` must be a dependency — with [] the callback
    // captured the initial false and the Encrypt toggle never did anything.
  }, [encryptUploads, hostPlanTier, touchP2pActivity, requestFileList]);

  const listHostDirForSync = useCallback(
    (relPath: string): Promise<FileNode> => {
      return new Promise((resolve, reject) => {
        const peer = peerRef.current;
        if (!peer) {
          reject(new Error('not connected'));
          return;
        }
        pendingHostListRef.current = resolve;
        const frame = listFilesRequest(selectedSharePathRef.current || undefined, relPath);
        if (!peer.send(frame)) {
          pendingHostListRef.current = null;
          reject(new Error('control channel not open'));
          return;
        }
        window.setTimeout(() => {
          if (pendingHostListRef.current === resolve) {
            pendingHostListRef.current = null;
            reject(new Error('host listing timed out'));
          }
        }, 20_000);
      });
    },
    []
  );

  const uploadFileAtHostRel = useCallback(
    async (hostRelPath: string, file: File) => {
      await uploadFiles([file], '', {
        absoluteRel: hostRelPath.replace(/\\/g, '/'),
      });
    },
    [uploadFiles]
  );

  const downloadHostFileAsBlob = useCallback(async (hostRelPath: string): Promise<Blob> => {
    const peer = await (ensurePeerRef.current?.() ?? Promise.resolve(null));
    if (!peer?.transferReady) throw new Error('Transfer channel not ready');
    if ((dlMeta.current || upActive.current) && !syncTransferActiveRef.current) {
      throw new Error('Another transfer is in progress');
    }
    return new Promise((resolve, reject) => {
      syncTransferActiveRef.current = true;
      syncBlobWaitRef.current = { resolve, reject };
      dlMode.current = 'save';
      decryptPending.current = false;
      const leaf = hostRelPath.split('/').pop() ?? 'file';
      dlMeta.current = { name: leaf, size: 0, mime: '' };
      dlChunks.current = [];
      setTransfer({ dir: 'down', name: leaf, total: 0, done: 0 });
      if (!peer.sendXfer(JSON.stringify({ op: 'get', path: hostRelPath }))) {
        syncBlobWaitRef.current = null;
        syncTransferActiveRef.current = false;
        dlMeta.current = null;
        setTransfer(null);
        reject(new Error('transfer channel closed'));
      }
    });
  }, []);

  const requestThumb = useCallback((relPath: string) => {
    if (thumbUrls[relPath]) return;
    peerRef.current?.send(getThumbRequest(relPath));
  }, [thumbUrls]);

  // --- WebRTC session bootstrap --------------------------------------------

  /** Starts a peer session as OFFERER (STUN/direct first, then TURN if configured). */
  const startPeer = useCallback(async (sig: SignalingClient) => {
    if (peerRef.current || peerStartingRef.current) return;
    const me = authRef.current;
    if (!me) return;
    if (!pcIdRef.current || !selectedSharePathRef.current) return;
    if (
      phaseRef.current === 'picking' ||
      phaseRef.current === 'picking-folder' ||
      phaseRef.current === 'auth'
    ) {
      return;
    }
    await waitP2pOfferQuiet();
    if (peerRef.current || peerStartingRef.current) return;
    if (!pcIdRef.current || !selectedSharePathRef.current) return;
    const dialGen = dialGenerationRef.current;
    peerStartingRef.current = true;
    setBusy(true);
    setP2pDetail(null);
    setConnLogLines([]);
    appendConnLog('Connecting to your drive…');
    setSession((s) => ({ ...s, error: null }));
    pendingCandidates.current = [];
    answerApplied.current = false;

    const apiIce = await fetchIceServers(SERVER_URL);
    iceRef.current = apiIce;
    const steps = dialIceSteps(apiIce);

    const tryDial = (servers: RTCIceServer[], timeoutMs: number): Promise<boolean> =>
      new Promise((resolve) => {
        let settled = false;
        const finish = (ok: boolean) => {
          if (settled) return;
          settled = true;
          if (timer !== undefined) window.clearTimeout(timer);
          dialAttemptRef.current = false;
          resolve(ok);
        };
        let timer: number | undefined;
        if (timeoutMs > 0) {
          timer = window.setTimeout(() => {
            silentPeerCloseRef.current = true;
            peerRef.current?.close();
            peerRef.current = null;
            finish(false);
          }, timeoutMs);
        }

        dialAttemptRef.current = true;
        let controlOpen = false;
        let xferOpen = false;
        let lastConnIceState = '';
        let gatheringLogged = false;
        const channelsReady = () => {
          if (controlOpen && xferOpen) finish(true);
        };

        const peer = new PeerSession(
          {
            onLocalCandidate: (candidate) => {
              sig.send({
                type: 'ice-candidate',
                target: targetRef.current,
                pcId: pcIdRef.current || undefined,
                candidate,
              });
            },
            onIceStatus: (st) => {
              if (st.gatheringState === 'complete' && !gatheringLogged) {
                gatheringLogged = true;
                appendConnLog(formatGatheringSummary(st.candidates));
              }
              const cs = st.connectionState;
              if (
                cs !== 'new' &&
                cs !== 'connecting' &&
                cs !== lastConnIceState
              ) {
                lastConnIceState = cs;
                if (cs === 'connected') {
                  appendConnLog('Attempting connectivity');
                } else if (cs === 'failed') {
                  appendConnLog('Connectivity failed');
                } else if (cs === 'disconnected') {
                  appendConnLog('Connectivity interrupted');
                } else if (cs === 'closed') {
                  appendConnLog('Connection closed');
                }
              }
            },
            onRemoteAnswer: (sdp) => {
              if (!answerAppliesToCurrentOffer()) return;
              const p = peerRef.current;
              if (!p) return;
              void p
                .applyAnswer(sdp)
                .then(() => {
                  if (!answerAppliesToCurrentOffer()) return;
                  answerApplied.current = true;
                  setP2pDetail('Host response received');
                  return Promise.all(
                    pendingCandidates.current.splice(0).map((c) => p.addCandidate(c))
                  );
                })
                .catch((e) => console.error('applyAnswer failed', e));
            },
            onChannelOpen: (ch) => {
              touchP2pActivity();
              if (ch.label === DATA_CHANNEL_LABEL || ch.label === XFER_LABEL) {
                appendConnLog(`Channel "${ch.label}" open`);
              }
              setSession((s) => ({ ...s, phase: 'linked', error: null, hostBusy: false }));
              setBusy(false);
              setP2pDetail(null);
              void peer.describePathDetail().then((d) => {
                setIcePath(d.path);
                setIceIpFamily(d.ipFamily);
              });
              if (ch.label === XFER_LABEL) {
                xferRef.current = ch;
                ch.binaryType = 'arraybuffer';
                ch.onmessage = handleXferFrame;
                xferOpen = true;
                channelsReady();
                return;
              }
              ch.onmessage = (ev: MessageEvent<string>) => {
                touchP2pActivity();
                try {
                  const parsed = JSON.parse(ev.data) as {
                    node?: FileNode;
                    error?: string;
                    type?: string;
                  };
                  applyFilesChannelMessage(parsed);
                } catch {
                  console.warn('non-JSON data-channel frame ignored');
                }
              };
              requestFileList('');
              if (ch.label === DATA_CHANNEL_LABEL) {
                controlOpen = true;
                channelsReady();
              }
            },
            onConnectionLost: (reason) => {
              if (dialAttemptRef.current) {
                if (reason === 'failed' && peerRef.current) {
                  void logIceFailureDiag(peerRef.current.pc, (line) => appendConnLog(line));
                }
                appendConnLog(`Dial attempt ended: ${reason}`);
                dialAttemptRef.current = false;
                peerRef.current = null;
                answerApplied.current = false;
                finish(false);
                return;
              }
              const idleClose = silentPeerCloseRef.current;
              if (idleClose) silentPeerCloseRef.current = false;
              peerRef.current = null;
              answerApplied.current = false;
              xferRef.current = null;
              dlMeta.current = null;
              dlChunks.current = [];
              dlDiscard.current = false;
              streamSink.current = null;
              upActive.current = false;
              uploadCancelRef.current = false;
              decryptPending.current = false;
              if (putWait.current) {
                putOk.current = false;
                putWait.current();
                putWait.current = null;
              }
              setTransfer(null);
              setBusy(false);
              setIcePath(null);
              setIceIpFamily(null);
              if (idleClose) return;
              const hint =
                reason === 'failed'
                  ? 'Could not connect from this network. Try connecting through another WiFi or re-try after sometime from a different data network zone.'
                  : 'Connection to your drive was lost. Try again in a moment.';
              if (selectedSharePathRef.current) {
                leaveExplorerDueToFailure(hint);
              } else {
                setTree(null);
                setSession((s) => ({ ...s, phase: 'online', error: hint }));
              }
            },
          },
          servers
        );
        peerRef.current = peer;
        void peer
          .createOffer()
          .then((offer) => {
            const sent = sig.send({
              type: 'offer',
              target: targetRef.current,
              pcId: pcIdRef.current || undefined,
              sdp: offer,
            });
            if (!sent) finish(false);
            else {
              offerDialGenRef.current = dialGenerationRef.current;
              setP2pDetail('Connectivity offered');
              appendConnLog('Session offer sent');
            }
          })
          .catch(() => finish(false));
      });

    const releaseHostP2p = () => {
      if (!targetRef.current || !pcIdRef.current || !signalingOpenRef.current) return;
      sig.send({
        type: 'p2p-release',
        target: targetRef.current,
        pcId: pcIdRef.current,
      });
      lastP2pReleaseAtRef.current = Date.now();
    };

    const dialStillWanted = () =>
      dialGen === dialGenerationRef.current &&
      Boolean(pcIdRef.current && selectedSharePathRef.current) &&
      phaseRef.current !== 'picking' &&
      phaseRef.current !== 'picking-folder' &&
      phaseRef.current !== 'auth';

    let connected = false;
    for (let i = 0; i < steps.length; i++) {
      if (!dialStillWanted()) break;
      const step = steps[i];
      setIceDialPhase(step.phase);
      for (let attempt = 0; attempt < DIAL_ATTEMPTS_PER_PHASE; attempt++) {
        if (!dialStillWanted()) break;
        if (attempt > 0) {
          appendConnLog(`Retry ${attempt + 1}/${DIAL_ATTEMPTS_PER_PHASE} (${step.phase})`);
        }
        appendConnLog(
          `${userDialPhaseLabel(step.phase)} (${Math.round(step.timeoutMs / 1000)}s)`
        );
        const ok = await tryDial(step.servers, step.timeoutMs);
        if (!dialStillWanted()) break;
        if (ok) {
          connected = true;
          break;
        }
        peerRef.current = null;
        answerApplied.current = false;
        pendingCandidates.current = [];
      }
      if (connected || !dialStillWanted()) break;
      peerRef.current = null;
      answerApplied.current = false;
      pendingCandidates.current = [];
      // Phase 1 timed out or failed — host must drop the old PC before TURN phase offer.
      if (i < steps.length - 1) {
        releaseHostP2p();
        await new Promise((r) => setTimeout(r, DIAL_PHASE_GAP_MS));
      }
    }
    setIceDialPhase(null);

    if (!connected && dialStillWanted()) {
      releaseHostP2p();
      appendConnLog('All dial phases failed');
      setBusy(false);
      if (selectedSharePathRef.current) {
        leaveExplorerDueToFailure(
          'Could not connect from this network. Try connecting through another WiFi or re-try after sometime from a different data network zone.'
        );
      } else {
        setSession((s) => ({
          ...s,
          phase: 'online',
          error:
            'Could not connect from this network. Try connecting through another WiFi or re-try after sometime from a different data network zone.',
        }));
      }
    } else if (!connected) {
      setBusy(false);
    }
    peerStartingRef.current = false;
  }, [
    handleXferFrame,
    touchP2pActivity,
    requestFileList,
    applyFilesChannelMessage,
    leaveExplorerDueToFailure,
    appendConnLog,
  ]);
  startPeerRef.current = startPeer;

  const retryConnect = useCallback(() => {
    clearHostBusyRetry();
    setSession((s) => ({ ...s, hostBusy: false, error: null }));
    const sig = signalingRef.current;
    if (sig && pcIdRef.current && signalingOpenRef.current) {
      void startPeer(sig);
    }
  }, [clearHostBusyRetry, startPeer]);

  /** Re-open WebRTC when the user acts but the idle timer closed the peer. */
  const ensurePeer = useCallback(async (): Promise<PeerSession | null> => {
    touchP2pActivity();
    const sig = signalingRef.current;
    if (!sig || !pcIdRef.current) return peerRef.current;
    const existing = peerRef.current;
    if (existing?.transferReady) return existing;
    if (existing) {
      teardownPeer({ preserveExplorer: true, silent: true });
    }
    if (!signalingOpenRef.current) return null;
    if (peerStartingRef.current) {
      for (let i = 0; i < 60; i++) {
        const p = peerRef.current;
        if (p?.transferReady) return p;
        await new Promise((r) => setTimeout(r, 250));
      }
      return peerRef.current;
    }
    await startPeer(sig);
    for (let i = 0; i < 60; i++) {
      const p: PeerSession | null = peerRef.current;
      if (p?.transferReady) {
        requestFileList(explorerRelPathRef.current);
        return p;
      }
      await new Promise((r) => setTimeout(r, 250));
    }
    return peerRef.current;
  }, [touchP2pActivity, startPeer, teardownPeer, requestFileList]);
  ensurePeerRef.current = ensurePeer;

  /** Refreshes the file tree; re-dials the PC if the data channel is gone. */
  const refreshFiles = useCallback(() => {
    touchP2pActivity();
    void ensurePeer().then((p) => {
      if (p?.transferReady) requestFileList(explorerRelPathRef.current);
    });
  }, [ensurePeer, touchP2pActivity, requestFileList]);

  const listFolderAt = useCallback(
    (relPath: string) => {
      explorerRelPathRef.current = relPath;
      void ensurePeer().then((p) => {
        if (p?.transferReady) requestFileList(relPath);
      });
    },
    [ensurePeer, requestFileList]
  );

  // --- signaling message handling ------------------------------------------
  const handleSignal = useCallback(
    (msg: SignalingMessage) => {
      switch (msg.type) {
        case 'presence': {
          // When a specific PC is selected, ignore aggregate presence (no pcId)
          // or updates for other PCs — otherwise any host under the owner account
          // looks "online" (e.g. Windows up while Ubuntu is selected).
          if (!pcIdRef.current) break;
          if (msg.pcId && msg.pcId !== pcIdRef.current) break;
          if (!msg.pcId) break;
          const hostOnline = msg.roles.includes('host');
          setSession((s) => ({ ...s, hostOnline }));
          // Dial when browsing a share. queueMicrotask: presence can arrive in the same
          // turn as connectToShare before React commits phase 'online' (re-watch case).
          if (
            hostOnline &&
            selectedSharePathRef.current &&
            !peerRef.current &&
            !peerStartingRef.current &&
            signalingRef.current
          ) {
            const sig = signalingRef.current;
            queueMicrotask(() => {
              if (
                phaseRef.current === 'picking' ||
                phaseRef.current === 'picking-folder' ||
                phaseRef.current === 'auth'
              ) {
                return;
              }
              if (peerRef.current || peerStartingRef.current) return;
              void startPeer(sig);
            });
          }
          break;
        }
        case 'answer': {
          if (!msg.sdp || !peerRef.current) break;
          if (!answerAppliesToCurrentOffer()) {
            appendConnLogRef.current('Ignored stale host response');
            break;
          }
          appendConnLogRef.current('Host response received');
          void peerRef.current
            .applyAnswer(msg.sdp)
            .then(() => {
              if (!answerAppliesToCurrentOffer()) return;
              answerApplied.current = true;
              setP2pDetail('Host response received');
              return Promise.all(
                pendingCandidates.current
                  .splice(0)
                  .map((c) => peerRef.current?.addCandidate(c))
              );
            })
            .catch((e) => console.error('answer failed', e));
          break;
        }
        case 'ice-candidate': {
          if (!msg.candidate?.candidate?.trim()) break;
          if (!validCandidateInit(msg.candidate)) break;
          if (peerRef.current && answerApplied.current) {
            void peerRef.current.addCandidate(msg.candidate);
          } else {
            // Remote description not applied yet — queue it.
            pendingCandidates.current.push(msg.candidate);
          }
          break;
        }
        case 'peer-unavailable': {
          appendConnLogRef.current('Host not connected (peer-unavailable)');
          setBusy(false);
          if (selectedSharePathRef.current) {
            leaveExplorerDueToFailure('Desktop host is not connected');
          } else {
            setSession((s) => ({
              ...s,
              phase: 'online',
              hostBusy: false,
              error: 'Desktop host is not connected',
            }));
          }
          break;
        }
        case 'host-busy': {
          if (msg.pcId && pcIdRef.current && msg.pcId !== pcIdRef.current) break;
          appendConnLogRef.current(`Host busy (${msg.reason ?? 'session'})`);
          peerRef.current?.close();
          peerRef.current = null;
          peerStartingRef.current = false;
          answerApplied.current = false;
          pendingCandidates.current = [];
          setBusy(false);
          const hint =
            msg.reason === 'upload'
              ? 'This PC is busy with an upload. Try again in a few seconds.'
              : 'This PC is in use. Try again in a few seconds.';
          setSession((s) => ({
            ...s,
            phase: 'online',
            hostBusy: true,
            error: hint,
          }));
          scheduleHostBusyRetry();
          break;
        }
        default:
          break;
      }
    },
    [startPeer, scheduleHostBusyRetry, leaveExplorerDueToFailure]
  );

  const dialSelectedPC = useCallback(() => {
    const sig = signalingRef.current;
    const pc = selectedPCRef.current;
    if (!sig || !pc || !signalingOpenRef.current) return;
    sig.send({
      type: 'watch',
      target: pc.ownerUserId,
      pcId: pc.id,
    });
    // Presence + hostOnline will call startPeer; watch updates pc-specific presence.
  }, []);

  // Mobile tabs freeze timers; when the user returns, refresh signaling watch + P2P.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      touchP2pActivity();
      if (
        pcIdRef.current &&
        phaseRef.current !== 'picking' &&
        phaseRef.current !== 'picking-folder' &&
        phaseRef.current !== 'auth'
      ) {
        dialSelectedPC();
      }
      if (
        pcIdRef.current &&
        phaseRef.current !== 'picking' &&
        phaseRef.current !== 'picking-folder' &&
        phaseRef.current !== 'auth' &&
        !peerRef.current?.transferReady
      ) {
        void ensurePeerRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [touchP2pActivity, dialSelectedPC]);

  /** One signaling socket for the whole signed-in session (PC list + dial). */
  const ensureSignaling = useCallback(() => {
    const creds = authRef.current;
    if (!creds || signalingRef.current) return;

    const sig = new SignalingClient(WS_URL, creds.token, {
      onMessage: handleSignal,
      onStatus: (status) => {
        signalingOpenRef.current = status === 'open';
        setSession((s) => {
          const next = { ...s, status };
          if (status === 'open' && s.phase === 'connecting') {
            return { ...next, phase: 'online' };
          }
          return next;
        });
        if (status === 'open' && selectedPCRef.current && pcIdRef.current) {
          dialSelectedPC();
        }
      },
    });
    signalingRef.current = sig;
    sig.connect();
  }, [handleSignal, dialSelectedPC]);

  // --- session lifecycle: auth -> websocket ---------------------------------
  const enterSession = useCallback(
    (creds: AuthResponse) => {
      teardown();
      setAuth(creds);
      setSession({ phase: 'picking', status: 'idle', hostOnline: false, error: null, hostBusy: false });
      listPCs(creds.token)
        .then(setPcs)
        .catch(() => setSession((s) => ({ ...s, error: DRIVES_LIST_ERROR })));
      ensureSignaling();
      touchSessionActivity();
      resetBrowserNavStack({ hcNav: 0 });
    },
    [teardown, ensureSignaling, resetBrowserNavStack, touchSessionActivity]
  );

  // Item 6: open a specific shared folder on a PC (P2P + LIST_FILES).
  const connectToShare = useCallback(
    (folder: { path: string; name: string }, pcOverride?: PCInfo) => {
      const pc = pcOverride ?? selectedPCRef.current;
      if (!authRef.current || !pc) return;
      if (pcOverride) setSelectedPC(pcOverride);
      clearHostBusyRetry();
      teardownPeer();
      selectedSharePathRef.current = folder.path;
      setActiveShare(folder);
      setHostPlanTier(pc.planTier ?? null);
      setThumbUrls({});
      targetRef.current = pc.ownerUserId;
      pcIdRef.current = pc.id;
      setTree(null);
      explorerRelPathRef.current = '';
      clearListWait();
      const wsReady = signalingOpenRef.current;
      const nextPhase: Phase = wsReady ? 'online' : 'connecting';
      // setState is async; startPeer() runs in the same turn and must not see 'picking'.
      phaseRef.current = nextPhase;
      setSession((s) => ({
        ...s,
        phase: nextPhase,
        status: wsReady ? 'open' : s.status === 'open' ? 'open' : 'connecting',
        hostOnline: false,
        error: null,
        hostBusy: false,
      }));
      ensureSignaling();
      dialSelectedPC();
      const sig = signalingRef.current;
      if (sig && signalingOpenRef.current) {
        void startPeerRef.current?.(sig);
      }
      applyHistoryLevel(2, 'push');
    },
    [teardownPeer, ensureSignaling, dialSelectedPC, clearHostBusyRetry, clearListWait, applyHistoryLevel]
  );

  /** Dial the host/share named in a sync profile (same path as opening a drive). */
  const ensureConnectedForSync = useCallback(
    async (profile: SyncProfile): Promise<void> => {
      const pc = pcs.find((p) => p.id === profile.pcId);
      if (!pc) throw new Error('That host PC is no longer in your drive list.');
      const folder = pc.folders.find((f) => f.path === profile.hostSharePath);
      if (!folder) throw new Error('That shared folder is no longer available.');

      const ready =
        pcIdRef.current === profile.pcId &&
        selectedSharePathRef.current === profile.hostSharePath &&
        peerRef.current?.transferReady &&
        phaseRef.current === 'linked';

      if (ready) return;

      connectToShare(folder, pc);
      for (let i = 0; i < 180; i++) {
        await new Promise((r) => setTimeout(r, 500));
        if (
          pcIdRef.current === profile.pcId &&
          selectedSharePathRef.current === profile.hostSharePath &&
          peerRef.current?.transferReady &&
          phaseRef.current === 'linked'
        ) {
          return;
        }
      }
      throw new Error('Could not connect to the host for sync. Start the desktop app and try again.');
    },
    [pcs, connectToShare]
  );

  const runJobForProfile = useCallback(
    async (profile: SyncProfile, mode: SyncMode) => {
      await ensureConnectedForSync(profile);
      const tier =
        hostPlanTier ?? pcs.find((p) => p.id === profile.pcId)?.planTier ?? null;
      if (!isPaidTier(tier)) throw new Error(PAID_FEATURE_HINT);
      const localRoot = await loadDirHandle(profile.id);
      if (!localRoot) throw new Error('Local folder access expired — set up sync again.');
      try {
        await runProfileJob(profile, localRoot, mode, {
          listHostDir: listHostDirForSync,
          uploadToHost: uploadFileAtHostRel,
          downloadFromHost: downloadHostFileAsBlob,
          onProgress: () => undefined,
        });
      } finally {
        syncTransferActiveRef.current = false;
        setTransfer(null);
      }
      if (phaseRef.current === 'linked') {
        requestFileList(explorerRelPathRef.current);
      }
    },
    [
      ensureConnectedForSync,
      hostPlanTier,
      pcs,
      listHostDirForSync,
      uploadFileAtHostRel,
      downloadHostFileAsBlob,
      requestFileList,
    ]
  );

  const runDownloadForProfile = useCallback(
    async (profile: SyncProfile) => {
      await ensureConnectedForSync(profile);
      const tier =
        hostPlanTier ?? pcs.find((p) => p.id === profile.pcId)?.planTier ?? null;
      if (!isPaidTier(tier)) throw new Error(PAID_FEATURE_HINT);
      const localRoot = await loadDirHandle(profile.id);
      if (!localRoot) throw new Error('Local folder access expired — set up sync again.');
      try {
        await runProfileDownload(profile, localRoot, 'initial', {
          listHostDir: listHostDirForSync,
          uploadToHost: uploadFileAtHostRel,
          downloadFromHost: downloadHostFileAsBlob,
          onProgress: () => undefined,
        });
      } finally {
        syncTransferActiveRef.current = false;
        setTransfer(null);
      }
    },
    [
      ensureConnectedForSync,
      hostPlanTier,
      pcs,
      listHostDirForSync,
      uploadFileAtHostRel,
      downloadHostFileAsBlob,
    ]
  );

  /** Pick a PC; multiple shares on that PC go through the folder list first. */
  const selectPC = useCallback(
    (pc: PCInfo) => {
      if (!authRef.current) return;
      clearHostBusyRetry();
      teardownPeer();
      setSelectedPC(pc);
      selectedSharePathRef.current = '';
      setActiveShare(null);
      targetRef.current = '';
      pcIdRef.current = '';

      if (pc.folders.length === 0) {
        setSession((s) => ({
          ...s,
          phase: 'picking',
          error: 'This PC has no folders shared with you yet.',
        }));
        setSelectedPC(null);
        return;
      }

      if (pc.folders.length === 1) {
        connectToShare(pc.folders[0], pc);
        return;
      }

      setSession((s) => ({
        ...s,
        phase: 'picking-folder',
        hostOnline: false,
        error: null,
        hostBusy: false,
      }));
      applyHistoryLevel(1, 'push');
    },
    [clearHostBusyRetry, teardownPeer, connectToShare, applyHistoryLevel]
  );

  const goToFolderPicker = useCallback(() => {
    clearHostBusyRetry();
    clearListWait();
    teardownPeer();
    setTree(null);
    explorerRelPathRef.current = '';
    selectedSharePathRef.current = '';
    setActiveShare(null);
    targetRef.current = '';
    pcIdRef.current = '';
    setSession((s) => ({
      ...s,
      phase: 'picking-folder',
      hostOnline: false,
      error: null,
      hostBusy: false,
    }));
  }, [teardownPeer, clearHostBusyRetry, clearListWait]);

  const goToDrivePicker = useCallback(() => {
    clearHostBusyRetry();
    clearListWait();
    explorerConnectGenRef.current += 1;
    teardownPeer();
    setSelectedPC(null);
    targetRef.current = '';
    pcIdRef.current = '';
    selectedSharePathRef.current = '';
    setActiveShare(null);
    setTree(null);
    explorerRelPathRef.current = '';
    setSession((s) => ({
      ...s,
      phase: 'picking',
      hostOnline: false,
      error: null,
      hostBusy: false,
    }));
    const creds = authRef.current;
    if (creds) {
      listPCs(creds.token)
        .then(setPcs)
        .catch(() => setSession((s) => ({ ...s, error: DRIVES_LIST_ERROR })));
    }
  }, [teardownPeer, clearHostBusyRetry, clearListWait]);

  const backToFolders = useCallback(() => {
    goToFolderPicker();
    navHistoryLevelRef.current = 1;
  }, [goToFolderPicker]);

  const onAuth = useCallback(
    (creds: AuthResponse) => enterSession(creds),
    [enterSession]
  );

  const logout = useCallback((reason?: string) => {
    teardown();
    resetBrowserNavStack({ hcAuth: true });
    setAuth(null);
    setSelectedPC(null);
    setActiveShare(null);
    setPcs([]);
    setTree(null);
    selectedSharePathRef.current = '';
    targetRef.current = '';
    pcIdRef.current = '';
    // Item 2: drop this session's unlocked encryption password so a different
    // account signing in on the same device can never reuse it.
    setEncryptUploads(false);
    setEncPrompt(null);
    try {
      window.sessionStorage.removeItem('hc_enc_password');
    } catch {
      /* private mode */
    }
    setSession({ phase: 'auth', status: 'idle', hostOnline: false, error: reason ?? null, hostBusy: false });
  }, [teardown, resetBrowserNavStack]);

  // Full sign-out after session idle (closes signaling WebSocket; reduces idle server load).
  useEffect(() => {
    if (!auth || SESSION_IDLE_MS <= 0) return;
    sessionActivityRef.current = Date.now();
    const passive = { passive: true, capture: true } as const;
    const bump = () => touchSessionActivity();
    // Intentional user input only — not scroll (lists fire constantly) or P2P retries.
    document.addEventListener('pointerdown', bump, passive);
    document.addEventListener('keydown', bump, passive);
    document.addEventListener('touchstart', bump, passive);
    const id = window.setInterval(() => {
      if (!authRef.current) return;
      if (Date.now() - sessionActivityRef.current < SESSION_IDLE_MS) return;
      logout('Signed out after inactivity.');
    }, 30_000);
    return () => {
      document.removeEventListener('pointerdown', bump, passive);
      document.removeEventListener('keydown', bump, passive);
      document.removeEventListener('touchstart', bump, passive);
      window.clearInterval(id);
    };
  }, [auth, logout, touchSessionActivity]);

  // Item 6: leave the current PC and return to the "My PCs" picker without
  // signing out. Tears down the peer/signaling link, clears the selection, and
  // refreshes the PC list so any share changes made elsewhere are reflected.
  const backToPCs = useCallback(() => {
    goToDrivePicker();
    navHistoryLevelRef.current = 0;
  }, [goToDrivePicker]);

  // System/browser back (Android/iPhone): release P2P like in-app ← buttons.
  useEffect(() => {
    if (!auth) return;
    const onPopState = (e: PopStateEvent) => {
      if (navHistoryResetRef.current) return;

      const raw = e.state as { hcNav?: number; hcAuth?: true } | null;
      if (raw?.hcAuth || !authRef.current) {
        navHistoryLevelRef.current = 0;
        historyPushCountRef.current = 0;
        return;
      }

      const target =
        typeof raw?.hcNav === 'number' ? (raw.hcNav as 0 | 1 | 2) : 0;

      if (historyPushCountRef.current > 0) {
        historyPushCountRef.current -= 1;
      }

      const phase = phaseRef.current;
      const browsingShare =
        Boolean(selectedSharePathRef.current) ||
        phase === 'linked' ||
        phase === 'online' ||
        phase === 'connecting';

      if (browsingShare) {
        if (target === 1) goToFolderPicker();
        else goToDrivePicker();
      } else if (phase === 'picking-folder' && target === 0) {
        goToDrivePicker();
      } else if (pcIdRef.current || peerRef.current) {
        teardownPeer();
      }
      navHistoryLevelRef.current = target;
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [auth, goToFolderPicker, goToDrivePicker, teardownPeer]);

  // Item 2: switching Encrypt on requires an unlocked password for THIS
  // session. If none is cached yet, prompt to initialise it (account has no
  // verifier) or unlock the existing one before the toggle takes effect.
  const handleToggleEncrypt = useCallback(() => {
    if (encryptUploads) {
      setEncryptUploads(false);
      return;
    }
    let stored: string | null = null;
    try {
      stored = window.sessionStorage.getItem('hc_enc_password');
    } catch {
      stored = null; // private mode — must prompt
    }
    if (stored) {
      setEncryptUploads(true);
      return;
    }
    setEncPrompt(authRef.current?.hasEncryptionPassword ? 'unlock' : 'init');
  }, [encryptUploads]);

  /** Item 2: the prompt verified/created the password — cache + enable. */
  const onEncUnlock = useCallback((password: string) => {
    try {
      window.sessionStorage.setItem('hc_enc_password', password);
    } catch {
      /* private mode — upload will re-prompt next time */
    }
    setEncryptUploads(true);
    setEncPrompt(null);
  }, []);

  const pcSessionActive = Boolean(
    auth && selectedPC && activeShare && session.phase !== 'picking' && session.phase !== 'picking-folder'
  );
  const inExplorer =
    auth &&
    session.phase !== 'auth' &&
    session.phase !== 'picking' &&
    session.phase !== 'picking-folder';

  const connLogActive =
    busy ||
    connLogExpanded ||
    (Boolean(inExplorer) && connLogLines.length > 0 && session.phase !== 'linked');

  // --- render ---------------------------------------------------------------
  return (
    <div className={`app${connLogExpanded ? ' conn-log-expanded' : ''}`}>
      <header className="topbar drive-topbar">
        <div className="brand">
          <span className="brand-mark drive-logo" aria-hidden>
            <svg width="28" height="28" viewBox="0 0 24 24">
              <path fill="#1a73e8" d="M12 2L3 7v10l9 5 9-5V7l-9-5zm0 2.2l6.5 3.6L12 11.4 5.5 7.8 12 4.2zM5 9.4l6 3.3v6.6l-6-3.3V9.4zm14 0v6.6l-6 3.3v-6.6l6-3.3z" />
            </svg>
          </span>
          <h1>{APP_NAME}</h1>
        </div>
        {auth && (
          <div className="topbar-right">
            <StatusPills
              session={session}
              busy={busy}
              signedIn={!!auth}
              pcSessionActive={pcSessionActive}
              icePath={icePath}
              iceDialPhase={iceDialPhase}
              iceIpFamily={iceIpFamily}
              p2pDetail={p2pDetail}
            />
            <button className="btn ghost topbar-signout" onClick={() => logout()}>
              Sign out
            </button>
          </div>
        )}
      </header>

      {session.error && (
        <div className="banner error">
          <span>{session.error}</span>
          {session.hostBusy && (
            <button type="button" className="btn ghost banner-retry" onClick={retryConnect}>
              Retry now
            </button>
          )}
        </div>
      )}

      {(!auth || session.phase === 'auth') && <AuthGate onSubmit={onAuth} />}

      {auth && session.phase === 'picking' && (
        <main className="content drive-content">
          {auth && (
            <SyncJobsPanel
              key={syncJobsTick}
              userId={auth.userId}
              pcs={pcs}
              onRunJob={runJobForProfile}
              onDownload={runDownloadForProfile}
            />
          )}
          <DrivePicker pcs={pcs} onSelect={(pc) => selectPC(pc)} />
        </main>
      )}

      {auth && session.phase === 'picking-folder' && selectedPC && (
        <main className="content drive-content">
          {auth && (
            <SyncJobsPanel
              key={syncJobsTick}
              userId={auth.userId}
              pcs={pcs}
              onRunJob={runJobForProfile}
              onDownload={runDownloadForProfile}
            />
          )}
          <FolderPicker
            pc={selectedPC}
            onSelect={(folder) => connectToShare(folder)}
            onBack={backToPCs}
          />
        </main>
      )}

      {inExplorer && (
        <main className="content drive-content">
          <h2 className="drive-section-title">My Drives</h2>
          <div className="drive-session-nav">
            <button type="button" className="btn ghost drive-nav-btn" onClick={backToPCs}>
              ← My Drives
            </button>
            {selectedPC && selectedPC.folders.length > 1 && (
              <button type="button" className="btn ghost drive-nav-btn" onClick={backToFolders}>
                ← Shared folders
              </button>
            )}
            {session.phase === 'linked' && selectedPC && activeShare && auth && (
              <button
                type="button"
                className="btn drive-nav-btn"
                disabled={!isPaidTier(hostPlanTier)}
                title={!isPaidTier(hostPlanTier) ? PAID_FEATURE_HINT : undefined}
                onClick={() => setSyncSetupOpen(true)}
              >
                Set up sync…
              </button>
            )}
          </div>
          {!session.hostOnline && session.phase !== 'linked' && (
            <div className="waiting">
              <div className="spinner" />
              {session.status === 'open' ? (
                <>
                  <p>Waiting for {selectedPC?.name ?? 'your desktop host'} to come online…</p>
                  <p className="hint">
                    Start the Home Drive host app on your PC and sign in with the account that shared the folder.
                  </p>
                </>
              ) : session.status === 'connecting' || session.status === 'closed' ? (
                <p>Connecting to your account…</p>
              ) : (
                <p>Cannot reach the service right now. Check your network and try again.</p>
              )}
            </div>
          )}
          {busy && session.hostOnline && session.phase !== 'linked' && (
            <div className="waiting">
              <div className="spinner" />
              <p>Connecting to your drive…</p>
            </div>
          )}
          {session.phase === 'linked' && tree && (
            <>
            <FileExplorer
              root={tree}
              hostName={
                selectedPC && activeShare
                  ? `${selectedPC.name} · ${activeShare.name}`
                  : selectedPC?.name
              }
              planTier={hostPlanTier}
              thumbUrls={thumbUrls}
              onRequestThumb={requestThumb}
              onRefresh={refreshFiles}
              onListFolder={listFolderAt}
              onRelPathChange={(p) => {
                explorerRelPathRef.current = p;
              }}
              onOpenFile={openFile}
              onDownloadFile={downloadFile}
              onShareFile={shareFile}
              onDeleteEntry={deleteEntry}
              onMoveEntry={moveEntry}
              onUploadFiles={(files, destDir) => void uploadFiles(files, destDir)}
              onCreateFolder={createFolder}
              encrypt={encryptUploads}
              onToggleEncrypt={handleToggleEncrypt}
              transfer={transfer}
              onCancelTransfer={cancelTransfer}
            />
            </>
          )}
          {session.phase === 'linked' && !tree && (
            <div className="waiting">
              <div className="spinner" />
              <p>Requesting folder listing…</p>
            </div>
          )}
        </main>
      )}

      {preview && (
        <PreviewModal
          preview={preview}
          onClose={() => {
            // Closing a still-streaming preview mid-transfer: stop appending
            // and drop the remaining frames instead of buffering the rest of
            // the file into memory for a preview nobody is watching.
            if (preview.streaming && streamSink.current) {
              streamSink.current.close();
              streamSink.current = null;
              if (dlMeta.current) dlDiscard.current = true;
            }
            URL.revokeObjectURL(preview.url);
            setPreview(null);
          }}
        />
      )}

      {/* Item 2: one-time encryption-password initialisation / unlock prompt. */}
      {encPrompt && auth && (
        <EncVaultModal
          mode={encPrompt}
          token={auth.token}
          onUnlock={onEncUnlock}
          onCancel={() => setEncPrompt(null)}
        />
      )}

      {syncSetupOpen && auth && selectedPC && activeShare && tree && (
        <SyncSetupModal
          open={syncSetupOpen}
          onClose={() => setSyncSetupOpen(false)}
          userId={auth.userId}
          destination={{
            pcId: selectedPC.id,
            ownerUserId: selectedPC.ownerUserId,
            pcName: selectedPC.name,
            hostSharePath: activeShare.path,
            hostShareName: activeShare.name,
            defaultHostRelDir: explorerRelPathRef.current,
          }}
          planTier={hostPlanTier}
          shareRoot={tree}
          listHostDir={listHostDirForSync}
          onRunJob={runJobForProfile}
          onProfilesChanged={() => setSyncJobsTick((n) => n + 1)}
        />
      )}

      <ConnectivityLogPanel
        lines={connLogLines}
        active={connLogActive}
        expanded={connLogExpanded}
        onToggleExpanded={() => setConnLogExpanded((v) => !v)}
        onClear={() => setConnLogLines([])}
      />
    </div>
  );
}
